import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import {Nav, Tab} from 'react-bootstrap';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getUserWalletBalance, getUserWalletTopUpCount, getUserWalletOutflows, getUserTransactionLimits } from '../../../services/AccountService';
import { getAllExpenseTags, getLatestOutboundUserWalletTransactions } from '../../../services/TransactionService';
import { getDeferredTopUpsForWalletActivation } from '../../../services/LedgerService';
import { formatAmount } from '../../../utils/helpers';
import {connect} from 'react-redux';
import {ThemeContext} from "../../../context/ThemeContext";
import PaybillForm from './Dashboard/PaybillForm';
import BuyGoodsForm from './Dashboard/BuyGoodsForm';
import SendMoneyForm from './Dashboard/SendMoneyForm';
import PochiLaBiasharaForm from './Dashboard/PochiLaBiasharaForm';
import RequestTopUpForm from './Dashboard/RequestTopUpForm';
import TopUpVaultForm from './Dashboard/TopUpVaultForm';
import LatestPartnerTopUps from './Dashboard/LatestPartnerTopUps';
import MyWalletDashboardBalanceCardSlider from './Dashboard/MyWalletDashboardBalanceCardSlider';
import { usePartnerHasSoloUser } from '../../../hooks/usePartnerHasSoloUser';
import CounterpartyReveal from '../CounterpartyReveal';
import { formatReceiverPartyForDisplay } from '../../../utils/phoneFormat';
import { clickableRowProps } from '../../../utils/clickableRow';
import useGuidedTour from '../../../hooks/useGuidedTour';
import {
    MY_WALLET_DASHBOARD_TOUR_ID,
    buildMyWalletDashboardTourSteps,
} from './MyWalletDashboard.tour';

const UNSETTLED_ROW_POLL_CEILING_MS = 15 * 60 * 1000;

const hasFreshUnsettledTransaction = (transactionsData) => {
    const rows = transactionsData?.data?.data;
    if (!Array.isArray(rows) || rows.length === 0) return false;
    const cutoff = Date.now() - UNSETTLED_ROW_POLL_CEILING_MS;
    return rows.some((row) => {
        if (row?.post_mpesa_result_code !== null && row?.post_mpesa_result_code !== undefined) return false;
        const createdAt = row?.created_at ? new Date(row.created_at).getTime() : null;
        return createdAt !== null && createdAt >= cutoff;
    });
};

// Render-prop wrapper that gives each transaction row its own reveal-open
// state so a whole-row click can drive the same details modal the eye
// badge opens. The wrapper is inlined here rather than shared because
// each list has its own row shape and the useState per row is what
// keeps the reveal state truly local.
const RevealRow = ({ children }) => {
    const [isRevealOpen, setIsRevealOpen] = useState(false);
    return children({ isRevealOpen, setIsRevealOpen });
};

const MyWalletDashboard = ({authUser}) => {
    const queryClient = useQueryClient();

    // Solo-user partners never see the vault dashboard, so the top-up flow
    // and the live top-up feed both need to live here. hasSoloUser drives
    // the swap of the prominent Request Top-Up button (which asks the
    // admin to disburse) for a Top Up button that fires a real STK push
    // the backend auto-disburses into this wallet on settlement.
    //
    // hasSoloUserLoading gates every solo-vs-team branch on this dashboard
    // so nobody ever sees the wrong shape during the load window. The
    // cached value is returned instantly on every subsequent mount, so
    // the spinner only appears the first time in a browser session.
    const { hasSoloUser, isLoading: hasSoloUserLoading } = usePartnerHasSoloUser(authUser?.partner_id);

    // Deferred top-ups: only fetched when the user is a solo super admin
    // without an active wallet, since the endpoint refuses anyone else and
    // the banner it feeds only renders in that shape.
    const deferredEnabled = !!authUser?.partner_id && hasSoloUser && !authUser?.has_active_wallet;
    const { data: deferredTopUpsData } = useQuery({
        queryKey: ['deferredTopUpsForWalletActivation', authUser?.partner_id],
        queryFn: () => getDeferredTopUpsForWalletActivation(authUser.partner_id).then(res => res.data),
        enabled: deferredEnabled,
        staleTime: 30 * 1000,
    });
    const deferredTopUpsCount = deferredTopUpsData?.count ?? 0;
    const deferredTopUpsTotal = deferredTopUpsData?.total_amount ?? '0.00';

    // Sidebar Top Up shortcut for solo users passes action=top-up so the
    // wallet dashboard opens on the Top Up tab directly rather than
    // starting on the default Buy Goods tab. Only meaningful when the
    // Top Up tab renders (solo + wallet active).
    const [searchParams] = useSearchParams();
    const initialTabKey = searchParams.get('action') === 'top-up' && hasSoloUser
        ? 'NavTopUpWallet'
        : 'NavBuyGoods';

    const { data: walletBalanceData, isLoading: userWalletBalanceLoading } = useQuery({
        queryKey: ['walletBalance', authUser?.id],
        queryFn: () => getUserWalletBalance(authUser.id),
        enabled: !!authUser,
        staleTime: 0,
        // Piggyback on the transactions poll: while an unsettled row
        // exists on the latest transactions cache, refetch the balance
        // every four seconds so the number ticks in step with the row
        // it corresponds to. Poll stops the moment nothing is in flight.
        refetchInterval: () => {
            const cached = queryClient.getQueryData(['latestTransactions', authUser?.id]);
            return hasFreshUnsettledTransaction(cached) ? 4000 : false;
        },
    });
    const userWalletBalance = walletBalanceData?.data?.balance ?? null;

    const { data: topUpCountData, isLoading: userWalletTopUpCountLoading } = useQuery({
        queryKey: ['walletTopUpCount', authUser?.id],
        queryFn: () => getUserWalletTopUpCount(authUser.id),
        enabled: !!authUser,
        staleTime: 0,
    });
    const userWalletTopUpCount = topUpCountData?.data?.userWalletTopUpCount ?? null;

    const { data: outflowsData, isLoading: userWalletOutflowCountLoading } = useQuery({
        queryKey: ['walletOutflows', authUser?.id],
        queryFn: () => getUserWalletOutflows(authUser.id),
        enabled: !!authUser,
        staleTime: 0,
    });
    const userWalletOutflowCount = outflowsData?.data?.userWalletOutflows ?? null;

    const { data: expenseTagsData, isLoading: expenseTagsLoading } = useQuery({
        queryKey: ['allExpenseTags', authUser?.partner_id],
        queryFn: () => getAllExpenseTags(authUser.partner_id),
        enabled: !!authUser,
        staleTime: 5 * 60 * 1000,
    });
    const expenseTags = expenseTagsData?.data?.data ?? null;

    const { data: latestTransactionsData, isLoading: latestOutboundUserWalletTransactionsLoading } = useQuery({
        queryKey: ['latestTransactions', authUser?.id],
        queryFn: () => getLatestOutboundUserWalletTransactions(authUser.id, 1, 5),
        enabled: !!authUser,
        staleTime: 0,
        refetchInterval: (query) => (hasFreshUnsettledTransaction(query.state.data) ? 4000 : false),
    });
    const latestOutboundUserWalletTransactions = latestTransactionsData?.data?.data ?? null;

    const outboundStatusBadge = (transaction) => {
        const code = transaction?.post_mpesa_result_code;
        if (code === null || code === undefined) return { cls: 'text-warning', label: 'Pending' };
        if (String(code) === '0')                 return { cls: 'text-success', label: 'Successful' };
        return { cls: 'text-danger', label: 'Failed' };
    };

    const formatWalletAmount = (amount) =>
        amount != null
            ? `KES ${Number(amount).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`
            : null;

    const formatWalletTransactionDate = (dateString) =>
        new Date(dateString).toLocaleString('en-GB', {
            day: 'numeric', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });

    const { data: transactionLimitsData, isLoading: transactionLimitsLoading, isError: transactionLimitsError } = useQuery({
        queryKey: ['transactionLimits', authUser?.id],
        queryFn: () => getUserTransactionLimits(authUser.partner_id, authUser.id),
        enabled: !!authUser,
        staleTime: 0,
    });
    const transactionLimits = transactionLimitsData?.data?.data ?? [];

    const {changeBackground} = useContext(ThemeContext);

    useEffect(() => {
        changeBackground({value: "light", label: "Light"});
    }, []);

    useEffect(() => {
        if (transactionLimitsError) toast.error('Failed to load transaction limits. Please try again.');
    }, [transactionLimitsError]);

    const latestTransactionsRef = useRef(null);

    const refreshDashboard = () => {
        queryClient.invalidateQueries({ queryKey: ['walletBalance', authUser.id] });
        queryClient.invalidateQueries({ queryKey: ['walletOutflows', authUser.id] });
        queryClient.invalidateQueries({ queryKey: ['latestTransactions', authUser.id] });
        queryClient.invalidateQueries({ queryKey: ['transactionLimits', authUser.id] });
    }

    const scrollLatestTransactionsIntoViewOnMobile = () => {
        if (typeof window === 'undefined') return;
        const isMobileViewport = window.matchMedia('(max-width: 767.98px)').matches;
        if (!isMobileViewport) return;
        const targetElement = latestTransactionsRef.current;
        if (!targetElement) return;
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const fixedHeader = document.querySelector('.header');
        const fixedHeaderHeight = fixedHeader ? fixedHeader.getBoundingClientRect().height : 0;
        const breathingRoom = 8;
        const targetTop = targetElement.getBoundingClientRect().top + window.pageYOffset - fixedHeaderHeight - breathingRoom;
        window.scrollTo({
            top: Math.max(targetTop, 0),
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
        });
    }

    const handleTransactionSubmitted = () => {
        refreshDashboard();
        scrollLatestTransactionsIntoViewOnMobile();
    }

    const navigate = useNavigate();
    const walletDashboardTourSteps = useMemo(
        () => buildMyWalletDashboardTourSteps(navigate, { hasSoloUser }),
        [navigate, hasSoloUser]
    );
    useGuidedTour(
        MY_WALLET_DASHBOARD_TOUR_ID,
        walletDashboardTourSteps,
        { enabled: !!authUser }
    );

    return (
        <>
            <div className="row">
                <div className="col-xl-8 order-2 order-xl-1">
                    <div className="row justify-content-md-center">
                        <div className="col-xl-5">
                            <div className="card card-wiget card-widget-mili" data-tour="wallet-balance-card">
                                <div className="card-body">
                                    <div className="card-wiget-info">
                                        <h4 className="count-num text-center">
                                            {userWalletBalanceLoading ? (
                                                <span className="spinner-border spinner-border-sm text-primary ms-2"
                                                      role="status"/>
                                            ) : (
                                                <p>{userWalletBalance}</p>
                                            )}
                                        </h4>
                                        <p className='text-center'>User Wallet Balance</p>
                                        <button
                                            type="button"
                                            className="position-absolute top-0 end-0 m-2 d-flex align-items-center justify-content-center"
                                            onClick={() => refreshDashboard()}
                                            title="Refresh Balance"
                                            data-tour="wallet-refresh"
                                            style={{
                                                width: '32px',
                                                height: '32px',
                                                borderRadius: '50%',
                                                border: '2px solid #198754',
                                                color: '#198754',
                                                background: 'white',
                                                padding: 0,
                                            }}
                                        >
                                            <i className="bi bi-arrow-clockwise"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-12">
                            <MyWalletDashboardBalanceCardSlider
                                authUser={authUser}
                                userWalletTopUpCountLoading={userWalletTopUpCountLoading}
                                userWalletTopUpCount={userWalletTopUpCount}
                                userWalletOutflowCount={userWalletOutflowCount}
                                userWalletOutflowCountLoading={userWalletOutflowCountLoading}
                            />
                        </div>
                        {(transactionLimitsLoading || transactionLimits.length > 0) && (
                            <div className="col-12">
                                <div className="card shadow-sm border-0">
                                    <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                                        <h2 className="heading h5 mb-0 fw-bold text-secondary">Spending Limits</h2>
                                    </div>
                                    <div className="card-body">
                                        {transactionLimitsLoading ? (
                                            <div className="text-center py-3">
                                                <div className="spinner-border spinner-border-sm text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </div>
                                        ) : (
                                            transactionLimits.map((limit) => {
                                                if (limit.amount_used === null) {
                                                    return (
                                                        <div key={limit.id} className="d-flex justify-content-between align-items-center mb-4">
                                                            <span className="fw-medium text-dark">{limit.description}</span>
                                                            <span className="badge bg-secondary">
                                                                Max: {formatAmount(limit.amount_limit)}
                                                            </span>
                                                        </div>
                                                    );
                                                }

                                                const cap       = Number(limit.amount_limit);
                                                const used      = Number(limit.amount_used);
                                                const pctUsed   = cap > 0 ? Math.min(100, (used / cap) * 100) : 0;
                                                const barColor  = pctUsed >= 90 ? 'bg-danger' : pctUsed >= 70 ? 'bg-warning' : 'bg-primary';

                                                return (
                                                    <div key={limit.id} className="mb-4">
                                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <span className="fw-medium text-dark">{limit.description}</span>
                                                                {limit.type === 'temporary' && (
                                                                    <span className="badge bg-warning text-dark">Temporary</span>
                                                                )}
                                                            </div>
                                                            <span className="text-muted small">
                                                                {formatAmount(used)} / {formatAmount(cap)} used
                                                            </span>
                                                        </div>
                                                        <div className="progress" style={{ height: '8px', borderRadius: '4px' }}>
                                                            <div
                                                                className={`progress-bar ${barColor}`}
                                                                style={{ width: `${pctUsed}%`, borderRadius: '4px' }}
                                                            />
                                                        </div>
                                                        <div className="d-flex justify-content-between mt-1">
                                                            <span className="text-muted small">
                                                                {formatAmount(limit.amount_remaining)} remaining
                                                            </span>
                                                            {limit.expires_on && (
                                                                <span className="text-muted small">Expires {limit.expires_on}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="col-12" ref={latestTransactionsRef}>
                            <div
                                className="card shadow-sm border-0 border-start border-4 border-success">
                                {/* Green left-border accent distinguishes wallet activity from partner top-ups. */}

                                {/* Header Section */}
                                <div
                                    className="card-header bg-white py-3 d-flex justify-content-between align-items-center position-relative"
                                    data-tour="latest-transactions">
                                    <h2 className="heading h5 mb-0 fw-bold text-secondary">Latest Transactions</h2>

                                    <div className="d-flex align-items-center gap-2">
                                    {/* Refined Refresh Button */}
                                    <button
                                        type="button"
                                        className="btn btn-link p-0 text-success d-flex align-items-center justify-content-center"
                                        onClick={() => refreshDashboard()}
                                        title="Refresh Balance"
                                        style={{
                                            width: '36px',
                                            height: '36px',
                                            borderRadius: '50%',
                                            backgroundColor: '#f0fdf4', // Light green bg
                                            border: '1px solid #dcfce7',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.transform = 'rotate(180deg)'}
                                        onMouseLeave={(e) => e.currentTarget.style.transform = 'rotate(0deg)'}
                                    >
                                        <i className="bi bi-arrow-clockwise fs-5"></i>
                                    </button>
                                    </div>
                                </div>

                                <div className="card-body p-0">
                                    {latestOutboundUserWalletTransactionsLoading ? (
                                        <div className="d-flex justify-content-center py-4">
                                            <span className="spinner-border spinner-border-sm text-primary" role="status" />
                                        </div>
                                    ) : latestOutboundUserWalletTransactions?.length > 0 ? (
                                        <>
                                            <div className="table-responsive d-none d-xl-block px-3 pt-2">
                                                <table className="table verticle-middle mb-0">
                                                    <thead>
                                                        <tr>
                                                            <th scope="col">Receipt</th>
                                                            <th scope="col">Amount</th>
                                                            <th scope="col">Recipient</th>
                                                            <th scope="col" className="text-end">Cost</th>
                                                            <th scope="col">Status</th>
                                                            <th scope="col" className="text-end">Date</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {latestOutboundUserWalletTransactions.map((transaction) => {
                                                            const { cls, label } = outboundStatusBadge(transaction);
                                                            const isClickable = Boolean(transaction.is_own_payout);
                                                            return (
                                                                <RevealRow key={transaction.id}>
                                                                    {({ isRevealOpen, setIsRevealOpen }) => (
                                                                        <tr {...clickableRowProps(() => setIsRevealOpen(true), isClickable)}>
                                                                            <td className="text-muted" style={{ overflowWrap: 'anywhere' }}>
                                                                                <span className="d-inline-flex align-items-center gap-2">
                                                                                    <span>{transaction.post_mpesa_receipt_number || '-'}</span>
                                                                                    {transaction.is_own_payout && (
                                                                                        <CounterpartyReveal payoutId={transaction.id} maskedValue={transaction.receiver_party || '-'} payout={transaction} requiresFetch={transaction.can_reveal_counterparty} isOpen={isRevealOpen} onOpenChange={setIsRevealOpen} hideMasked />
                                                                                    )}
                                                                                </span>
                                                                            </td>
                                                                            <td className="fw-medium text-dark">
                                                                                {transaction.amount != null
                                                                                    ? formatWalletAmount(transaction.amount)
                                                                                    : <span className="text-muted fst-italic">Pending</span>}
                                                                            </td>
                                                                            <td className="text-muted" style={{ overflowWrap: 'anywhere' }}>
                                                                                {transaction.receiver_party ? formatReceiverPartyForDisplay(transaction.receiver_party) : '-'}
                                                                            </td>
                                                                            <td className="text-end text-muted">
                                                                                {transaction.total_transaction_cost != null
                                                                                    ? formatWalletAmount(transaction.total_transaction_cost)
                                                                                    : '-'}
                                                                            </td>
                                                                            <td className={`fw-medium ${cls}`}>{label}</td>
                                                                            <td className="text-end text-muted small">
                                                                                {formatWalletTransactionDate(transaction.post_mpesa_transaction_time ?? transaction.created_at)}
                                                                            </td>
                                                                        </tr>
                                                                    )}
                                                                </RevealRow>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div className="table-responsive d-none d-md-block d-xl-none px-3 pt-2">
                                                <table className="table verticle-middle mb-0">
                                                    <thead>
                                                        <tr>
                                                            <th scope="col">Amount</th>
                                                            <th scope="col">Recipient / Receipt</th>
                                                            <th scope="col">Status</th>
                                                            <th scope="col" className="text-end">Date</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {latestOutboundUserWalletTransactions.map((transaction) => {
                                                            const { cls, label } = outboundStatusBadge(transaction);
                                                            const isClickable = Boolean(transaction.is_own_payout);
                                                            return (
                                                                <RevealRow key={transaction.id}>
                                                                    {({ isRevealOpen, setIsRevealOpen }) => (
                                                                        <tr {...clickableRowProps(() => setIsRevealOpen(true), isClickable)}>
                                                                            <td className="fw-medium text-dark">
                                                                                {transaction.amount != null
                                                                                    ? formatWalletAmount(transaction.amount)
                                                                                    : <span className="text-muted fst-italic">Pending</span>}
                                                                            </td>
                                                                            <td className="text-muted" style={{ overflowWrap: 'anywhere' }}>
                                                                                {transaction.receiver_party && (
                                                                                    <div>
                                                                                        {transaction.is_own_payout
                                                                                            ? <CounterpartyReveal payoutId={transaction.id} maskedValue={transaction.receiver_party} payout={transaction} requiresFetch={transaction.can_reveal_counterparty} isOpen={isRevealOpen} onOpenChange={setIsRevealOpen} />
                                                                                            : formatReceiverPartyForDisplay(transaction.receiver_party)}
                                                                                    </div>
                                                                                )}
                                                                                {transaction.post_mpesa_receipt_number && (
                                                                                    <div className="small">{transaction.post_mpesa_receipt_number}</div>
                                                                                )}
                                                                                {!transaction.receiver_party && !transaction.post_mpesa_receipt_number && (
                                                                                    <span className="fst-italic small">Awaiting M-Pesa confirmation</span>
                                                                                )}
                                                                            </td>
                                                                            <td className={`fw-medium ${cls}`}>{label}</td>
                                                                            <td className="text-end text-muted small">
                                                                                {formatWalletTransactionDate(transaction.post_mpesa_transaction_time ?? transaction.created_at)}
                                                                            </td>
                                                                        </tr>
                                                                    )}
                                                                </RevealRow>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                            <ul className="list-group list-group-flush d-block d-md-none">
                                                {latestOutboundUserWalletTransactions.map((transaction) => {
                                                    const { cls, label } = outboundStatusBadge(transaction);
                                                    const hasReference = transaction.receiver_party || transaction.post_mpesa_receipt_number;
                                                    const isClickable = Boolean(transaction.is_own_payout);
                                                    return (
                                                        <RevealRow key={transaction.id}>
                                                            {({ isRevealOpen, setIsRevealOpen }) => (
                                                                <li
                                                                    className="list-group-item py-3"
                                                                    {...clickableRowProps(() => setIsRevealOpen(true), isClickable)}
                                                                >
                                                                    <div className="d-flex gap-3">
                                                                        <div
                                                                            className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${cls}`}
                                                                            style={{ width: 40, height: 40, background: 'rgba(108, 117, 125, 0.08)' }}
                                                                        >
                                                                            <i className="bi bi-arrow-up-circle" style={{ fontSize: '1.25rem' }} />
                                                                        </div>
                                                                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                                            <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                                                                                <span className="fw-semibold text-body">
                                                                                    {transaction.amount != null
                                                                                        ? formatWalletAmount(transaction.amount)
                                                                                        : <span className="text-muted fst-italic">Pending</span>}
                                                                                </span>
                                                                                <span className={`fw-medium ${cls} text-nowrap small`}>{label}</span>
                                                                            </div>
                                                                            <div className="small text-muted mb-1">
                                                                                {formatWalletTransactionDate(transaction.post_mpesa_transaction_time ?? transaction.created_at)}
                                                                            </div>
                                                                            {hasReference ? (
                                                                                <>
                                                                                    {transaction.receiver_party && (
                                                                                        <div className="small text-muted" style={{ overflowWrap: 'anywhere' }}>
                                                                                            To {formatReceiverPartyForDisplay(transaction.receiver_party)}
                                                                                            {transaction.is_own_payout && (
                                                                                                <CounterpartyReveal payoutId={transaction.id} maskedValue={transaction.receiver_party} payout={transaction} requiresFetch={transaction.can_reveal_counterparty} isOpen={isRevealOpen} onOpenChange={setIsRevealOpen} hideMasked hideEye />
                                                                                            )}
                                                                                        </div>
                                                                                    )}
                                                                                    {transaction.post_mpesa_receipt_number && (
                                                                                        <div className="small text-muted" style={{ overflowWrap: 'anywhere' }}>
                                                                                            Receipt {transaction.post_mpesa_receipt_number}
                                                                                        </div>
                                                                                    )}
                                                                                </>
                                                                            ) : (
                                                                                <div className="small text-muted fst-italic">
                                                                                    Awaiting M-Pesa confirmation
                                                                                </div>
                                                                            )}
                                                                            <div className="d-flex justify-content-between align-items-end gap-2 mt-1">
                                                                                {transaction.total_transaction_cost != null ? (
                                                                                    <div className="small text-muted">
                                                                                        Cost {formatWalletAmount(transaction.total_transaction_cost)}
                                                                                    </div>
                                                                                ) : <span />}
                                                                                {isClickable && (
                                                                                    <span className="small text-primary fw-medium d-inline-flex align-items-center gap-1">
                                                                                        <i className="fa fa-eye" aria-hidden="true" />
                                                                                        View
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </li>
                                                            )}
                                                        </RevealRow>
                                                    );
                                                })}
                                            </ul>
                                        </>
                                    ) : (
                                        <p className="text-center text-muted py-4 mb-0">No transactions found.</p>
                                    )}
                                </div>
                                <div className="text-end px-3 py-2 border-top" data-tour="wallet-view-more">
                                    <Link to="/my-activity?tab=payouts" className="btn btn-link btn-sm p-0">
                                        View more
                                    </Link>
                                </div>
                            </div>
                        </div>
                        {hasSoloUser && (
                            <LatestPartnerTopUps
                                partnerId={authUser?.partner_id}
                                viewMoreLink="/my-activity?tab=disbursements"
                            />
                        )}
                    </div>
                </div>
                <div className="col-xl-4 order-1 order-xl-2">
                    <div className="row">
                        <div className="col-12">
                            <div className="card h-auto" data-tour="transaction-actions">
                                <div className="card-body px-0 pt-1">
                                    {authUser.has_active_wallet ? (
                                        <Tab.Container defaultActiveKey={initialTabKey}>
                                            <div className="">
                                                <div className="buy-sell">
                                                    {/*
                                                        Prominent action above the spend tabs. Solo users
                                                        get a Top Up button (direct STK push into their
                                                        wallet). Team users get a Request Top-Up button
                                                        (asks a super admin to disburse into their wallet).
                                                        The slot renders a spinner during the solo check
                                                        load window so nobody sees the wrong variant. The
                                                        check is cached across the session, so the spinner
                                                        only appears on the very first render.
                                                    */}
                                                    <div className="px-3 pt-3 pb-2 d-flex justify-content-center" style={{ minHeight: '3rem' }}>
                                                        {hasSoloUserLoading ? (
                                                            <span className="spinner-border spinner-border-sm text-primary" role="status" aria-label="Checking account type" />
                                                        ) : hasSoloUser ? (
                                                            <Nav.Link
                                                                as="button"
                                                                eventKey="NavTopUpWallet"
                                                                type="button"
                                                                className="btn btn-primary d-inline-flex align-items-center justify-content-center fw-semibold px-4 py-2"
                                                                style={{
                                                                    borderRadius: '2rem',
                                                                    boxShadow: '0 4px 14px rgba(115, 103, 240, 0.35)',
                                                                    letterSpacing: '0.05em',
                                                                    backgroundColor: '#7367F0',
                                                                    color: '#ffffff',
                                                                    border: 'none',
                                                                    textTransform: 'uppercase',
                                                                }}
                                                            >
                                                                <i className="bi bi-plus-circle-fill me-2 fs-5"></i>
                                                                <span>Top Up Wallet</span>
                                                            </Nav.Link>
                                                        ) : (
                                                            <Nav.Link
                                                                as="button"
                                                                eventKey="NavRequestTopUp"
                                                                type="button"
                                                                className="btn btn-primary d-inline-flex align-items-center justify-content-center fw-semibold px-4 py-2"
                                                                style={{
                                                                    borderRadius: '2rem',
                                                                    boxShadow: '0 4px 14px rgba(115, 103, 240, 0.35)',
                                                                    letterSpacing: '0.05em',
                                                                    backgroundColor: '#7367F0',
                                                                    color: '#ffffff',
                                                                    border: 'none',
                                                                    textTransform: 'uppercase',
                                                                }}
                                                            >
                                                                <i className="bi bi-envelope-plus-fill me-2 fs-5"></i>
                                                                <span>Request Top-Up</span>
                                                            </Nav.Link>
                                                        )}
                                                    </div>
                                                    <Nav className="nav nav-tabs spend-tabs" role="tablist" style={{ textTransform: 'uppercase' }}>
                                                        <Nav.Link as="button" className="nav-link" eventKey="NavBuyGoods"
                                                                  type="button" style={{ fontSize: '16px', fontWeight: 600, letterSpacing: '0.03em' }}>Buy Goods</Nav.Link>
                                                        <Nav.Link as="button" className="nav-link" eventKey="NavPaybill"
                                                                  type="button" style={{ fontSize: '16px', fontWeight: 600, letterSpacing: '0.03em' }}>Paybill</Nav.Link>
                                                        <Nav.Link as="button" className="nav-link" eventKey="NavSendMoney"
                                                                  type="button" style={{ fontSize: '16px', fontWeight: 600, letterSpacing: '0.03em' }}>Send Money</Nav.Link>
                                                        <Nav.Link as="button" className="nav-link" eventKey="NavPochi"
                                                                  type="button" style={{ fontSize: '16px', fontWeight: 600, letterSpacing: '0.03em' }}>Pochi</Nav.Link>
                                                    </Nav>
                                                </div>
                                                <Tab.Content>
                                                    <Tab.Pane eventKey="NavPaybill">
                                                        <Tab.Container defaultActiveKey="Navbuymarket">
                                                            <Tab.Content id="nav-tabContent1">
                                                                <Tab.Pane eventKey="Navbuymarket"></Tab.Pane>
                                                                <Tab.Pane eventKey="Navbuylimit"></Tab.Pane>
                                                            </Tab.Content>
                                                            <div className="sell-element">
                                                                <PaybillForm
                                                                    expenseTags={expenseTags}
                                                                    userWalletBalance={userWalletBalance}
                                                                    userWalletBalanceLoading={userWalletBalanceLoading}
                                                                    expenseTagsLoading={expenseTagsLoading}
                                                                    userId={authUser.id}
                                                                    onTransactionSubmitted={handleTransactionSubmitted}
                                                                />
                                                            </div>
                                                        </Tab.Container>
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="NavBuyGoods">
                                                        <Tab.Container defaultActiveKey="Navbuymarket">
                                                            <Tab.Content id="nav-tabContent1">
                                                                <Tab.Pane eventKey="Navbuymarket"></Tab.Pane>
                                                                <Tab.Pane eventKey="Navbuylimit"></Tab.Pane>
                                                            </Tab.Content>
                                                            <div className="sell-element">
                                                                <BuyGoodsForm
                                                                    expenseTags={expenseTags}
                                                                    expenseTagsLoading={expenseTagsLoading}
                                                                    userWalletBalance={userWalletBalance}
                                                                    userWalletBalanceLoading={userWalletBalanceLoading}
                                                                    userId={authUser.id}
                                                                    onTransactionSubmitted={handleTransactionSubmitted}
                                                                />
                                                            </div>
                                                        </Tab.Container>
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="NavSendMoney">
                                                        <Tab.Container defaultActiveKey="Navsellmarket">
                                                            <Tab.Content id="nav-tabContent2">
                                                                <Tab.Pane id="Navsellmarket"></Tab.Pane>
                                                                <Tab.Pane id="Navselllimit"></Tab.Pane>
                                                            </Tab.Content>
                                                            <div className="sell-element">
                                                                <SendMoneyForm
                                                                    expenseTags={expenseTags}
                                                                    expenseTagsLoading={expenseTagsLoading}
                                                                    userWalletBalance={userWalletBalance}
                                                                    userWalletBalanceLoading={userWalletBalanceLoading}
                                                                    userId={authUser.id}
                                                                    onTransactionSubmitted={handleTransactionSubmitted}
                                                                />
                                                            </div>
                                                        </Tab.Container>
                                                    </Tab.Pane>
                                                    <Tab.Pane eventKey="NavPochi">
                                                        <Tab.Container defaultActiveKey="Navpochimarket">
                                                            <Tab.Content id="nav-tabContent3">
                                                                <Tab.Pane eventKey="Navpochimarket"></Tab.Pane>
                                                            </Tab.Content>
                                                            <div className="sell-element">
                                                                <PochiLaBiasharaForm
                                                                    expenseTags={expenseTags}
                                                                    expenseTagsLoading={expenseTagsLoading}
                                                                    userWalletBalance={userWalletBalance}
                                                                    userWalletBalanceLoading={userWalletBalanceLoading}
                                                                    userId={authUser.id}
                                                                    onTransactionSubmitted={handleTransactionSubmitted}
                                                                />
                                                            </div>
                                                        </Tab.Container>
                                                    </Tab.Pane>
                                                    {!hasSoloUserLoading && (hasSoloUser ? (
                                                        <Tab.Pane eventKey="NavTopUpWallet">
                                                            <div className="sell-element">
                                                                <TopUpVaultForm
                                                                    partnerId={authUser.partner_id}
                                                                    variant="wallet"
                                                                />
                                                            </div>
                                                        </Tab.Pane>
                                                    ) : (
                                                        <Tab.Pane eventKey="NavRequestTopUp">
                                                            <div className="sell-element">
                                                                <RequestTopUpForm
                                                                    partnerId={authUser.partner_id}
                                                                />
                                                            </div>
                                                        </Tab.Pane>
                                                    ))}
                                                </Tab.Content>
                                            </div>
                                        </Tab.Container>
                                    ) : hasSoloUserLoading ? (
                                        <div className="text-center text-muted py-4 px-3">
                                            <span className="spinner-border spinner-border-sm text-primary" role="status" aria-label="Checking account type" />
                                        </div>
                                    ) : hasSoloUser ? (
                                        <div className="text-center text-muted py-4 px-3">
                                            <i className="bi bi-lock-fill fs-4 mb-2 d-block"></i>
                                            <p className="mb-1 fw-medium">Wallet not yet active</p>
                                            {deferredTopUpsCount > 0 ? (
                                                <div className="alert alert-info mx-auto mb-3" style={{ maxWidth: '420px' }}>
                                                    <div className="fw-bold mb-1">KES {formatAmount(deferredTopUpsTotal)} is waiting for your wallet.</div>
                                                    <small>
                                                        {deferredTopUpsCount === 1
                                                            ? 'One earlier top-up is parked because your wallet was not active when it landed. Activate your wallet to release it.'
                                                            : `${deferredTopUpsCount} earlier top-ups are parked because your wallet was not active when they landed. Activate your wallet to release them.`}
                                                    </small>
                                                </div>
                                            ) : (
                                                <small className="d-block mb-3">Activate your wallet before topping up or add users with wallets. A top-up needs a wallet to land in.</small>
                                            )}
                                            <div className="d-flex flex-wrap justify-content-center gap-2">
                                                <Link to="/user-profile" className="btn btn-primary btn-sm">
                                                    Activate wallet
                                                </Link>
                                                <Link to="/users?tour=add-user" className="btn btn-outline-primary btn-sm">
                                                    Add users
                                                </Link>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center text-muted py-4 px-3">
                                            <i className="bi bi-lock-fill fs-4 mb-2 d-block"></i>
                                            <p className="mb-1 fw-medium">Wallet not yet active</p>
                                            <small>Please verify your email address to enable payments and fund requests.</small>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

const mapStateToProps = (state) => {
    return {
        authUser: state.auth.authUser
    };
};

export default connect(mapStateToProps)(MyWalletDashboard);